/**
 * @file Email/password and optional Google sign-in page with safe post-login returnUrl.
 * @module frontend/src/pages/Login
 */

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { startGoogleLogin } from '../api/auth';
import { INPUT_MAX_LENGTH, PASSWORD_MAX_LENGTH, clampInput } from '../utils/inputLimits';
import { sanitizeReturnUrl } from '../constants';

/**
 * Sign-in form; navigates to sanitized returnUrl on success.
 * @returns {JSX.Element}
 */
export default function Login() {
  const { login, config } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnUrl = sanitizeReturnUrl(params.get('returnUrl'));
  const errorParam = params.get('error');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // REVIEW [LOW]: OAuth error query is rendered as text (React-escaped). Prefer a fixed
  // allowlist of error codes so unexpected messages are not user-visible.
  const [error, setError] = useState(
    errorParam ? `Sign-in error: ${errorParam.replace(/_/g, ' ')}` : ''
  );
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email, password);
      navigate(returnUrl, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 440, paddingTop: 40 }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <img
          src={`${process.env.PUBLIC_URL || ''}/dictatel-bid_logo_transparent.png`}
          alt="Dictatel-Bid"
          style={{
            height: 80,
            width: 'auto',
            maxWidth: '70%',
            objectFit: 'contain',
            marginBottom: 12,
          }}
        />
        <h1
          style={{
            fontSize: '1.35rem',
            fontWeight: 700,
            margin: '0 0 8px',
            lineHeight: 1.25,
            letterSpacing: '-0.02em',
          }}
        >
          Dictatel-Bid: Equip-Bid Auction Processor
        </h1>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: '0 0 6px' }}>Sign in</h2>
        <p style={{ color: '#555', margin: 0 }}>Access your company&apos;s auction groups.</p>
      </div>
      {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}
      <form onSubmit={onSubmit} className="card" style={{ padding: 20 }}>
        <label style={{ display: 'block', marginBottom: 12 }}>
          Email
          <input
            type="email"
            required
            maxLength={INPUT_MAX_LENGTH}
            value={email}
            onChange={(e) => setEmail(clampInput(e.target.value))}
            style={{ width: '100%', marginTop: 4 }}
            autoComplete="email"
          />
        </label>
        <label style={{ display: 'block', marginBottom: 16 }}>
          Password
          <input
            type="password"
            required
            maxLength={PASSWORD_MAX_LENGTH}
            value={password}
            onChange={(e) => setPassword(clampInput(e.target.value, PASSWORD_MAX_LENGTH))}
            style={{ width: '100%', marginTop: 4 }}
            autoComplete="current-password"
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      {config.googleEnabled && (
        <button
          type="button"
          className="btn"
          style={{ width: '100%', marginTop: 12 }}
          onClick={() => startGoogleLogin({ returnUrl })}
        >
          Sign in with Google
        </button>
      )}
      <p style={{ marginTop: 16 }}>
        <Link to={`/forgot-password`}>Forgot password?</Link>
        {config.allowOpenRegistration && (
          <>
            {' · '}
            <Link to={`/register?returnUrl=${encodeURIComponent(returnUrl)}`}>Create account</Link>
          </>
        )}
      </p>
    </div>
  );
}
